import {
  Component,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  Input,
} from '@angular/core';
import imageCompression from 'browser-image-compression';
import GIF from 'gif.js';

@Component({
    selector: 'app-gif-recorder',
    template: `
    <div class="gif-recorder">
      <div class="preview-container">
        <div class="preview" [ngClass]="{ recording: isRecording }">
          <video
            #videoPreview
            [hidden]="isPlayingBack"
            autoplay
            muted
            playsinline
          ></video>
          <img
            #previewImage
            [hidden]="!isPlayingBack"
            [src]="previewImageSrc"
          />
          <div
            class="camera-controls"
            *ngIf="!isPlayingBack && !isProcessing && !isLoading"
          >
            <button class="switch-camera-button" (click)="switchCamera()">
              <ion-icon name="camera-reverse-outline"></ion-icon>
            </button>
            <ng-container
              *ngIf="
                currentCameraFacingMode === 'environment' &&
                supportedLenses.length > 1
              "
            >
              <button
                *ngFor="let lens of supportedLenses"
                class="lens-button"
                [class.active]="lens === currentLens"
                (click)="switchLens(lens)"
              >
                {{ lens }}
              </button>
            </ng-container>
          </div>
          <div
            *ngIf="!isPlayingBack && !isProcessing && !isLoading"
            class="record-button"
            (touchstart)="startRecording()"
            (touchend)="stopRecording()"
            (mousedown)="startRecording()"
            (mouseup)="stopRecording()"
            (mouseleave)="stopRecording()"
          >
            <div class="record-icon" [class.recording]="isRecording"></div>
            <svg class="progress-ring" [class.active]="isRecording">
              <circle
                class="progress-ring__circle"
                stroke-width="4"
                fill="transparent"
                r="28"
                cx="30"
                cy="30"
                [style.stroke-dashoffset.px]="dashOffset"
              />
            </svg>
          </div>
          <div *ngIf="isProcessing || isLoading" class="loading-spinner"></div>
          <button
            *ngIf="isPlayingBack"
            class="remove-button"
            (click)="removeRecording()"
          >
            <ion-icon name="close-outline"></ion-icon>
          </button>
          <div
            class="blur-overlay"
            [class.active]="isLoading || isProcessing"
          ></div>
        </div>
      </div>
    </div>
  `,
    styles: [
        `
      .gif-recorder {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
      }
      .preview-container {
        width: 100%;
        max-width: 480px;
        aspect-ratio: 3 / 4;
        overflow: hidden;
      }
      .preview {
        width: 100%;
        height: 100%;
        position: relative;
        overflow: hidden;
      }
      .preview video,
      .preview img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .camera-controls {
        position: absolute;
        top: 10px;
        left: 10px;
        display: flex;
        gap: 10px;
        z-index: 10;
      }
      .switch-camera-button,
      .lens-button {
        background-color: rgba(255, 255, 255, 0.7);
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        font-size: 12px;
        font-weight: bold;
      }
      .lens-button.active {
        background-color: rgba(255, 255, 255, 0.9);
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
      }
      .record-button {
        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background-color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        touch-action: none;
        z-index: 10;
      }
      .record-icon {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background-color: red;
        transition: all 0.3s ease;
      }
      .record-icon.recording {
        width: 20px;
        height: 20px;
      }
      .progress-ring {
        position: absolute;
        width: 60px;
        height: 60px;
        transform: rotate(-90deg);
        opacity: 0;
        transition: opacity 0.3s ease;
      }
      .progress-ring.active {
        opacity: 1;
      }
      .progress-ring__circle {
        stroke: red;
        stroke-dasharray: 175.92;
      }
      .remove-button {
        position: absolute;
        top: 10px;
        right: 10px;
        background-color: rgba(255, 255, 255, 0.7);
        border: none;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        z-index: 10;
      }
      .remove-button ion-icon {
        font-size: 24px;
        color: #333;
      }
      .loading-spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 50px;
        height: 50px;
        border: 5px solid #f3f3f3;
        border-top: 5px solid #3498db;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        z-index: 11;
      }
      @keyframes spin {
        0% {
          transform: translate(-50%, -50%) rotate(0deg);
        }
        100% {
          transform: translate(-50%, -50%) rotate(360deg);
        }
      }
      .blur-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.5);
        backdrop-filter: blur(5px);
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
        z-index: 9;
      }
      .blur-overlay.active {
        opacity: 1;
        visibility: visible;
      }
    `,
    ],
    standalone: false
})
export class GifRecorderComponent implements OnInit, OnDestroy {
  @ViewChild('videoPreview') videoPreview!: ElementRef<HTMLVideoElement>;
  @ViewChild('previewImage') previewImage!: ElementRef<HTMLImageElement>;
  @Output() recordingComplete = new EventEmitter<File>();

  @Input() imageQuality = 0.7;
  @Input() resolutionScale = 0.5;
  @Input() maxWidth = 1920;
  @Input() maxHeight = 1080;
  @Input() gifMaxWidth = 854;
  @Input() gifMaxHeight = 480;
  @Input() compressionQuality = 0.7;
  @Input() targetPictureSize = 0.05;
  @Input() initialGifFrameQuality = 1;
  @Input() targetFPS = 10;
  @Input() set recordingDuration(value: number) {
    this._recordingDuration = value;
    this.circumference = 2 * Math.PI * 28;
    this.dashOffset = this.circumference;
  }
  get recordingDuration(): number {
    return this._recordingDuration;
  }

  isRecording = false;
  isPlayingBack = false;
  isProcessing = false;
  isLoading = true;
  previewImageSrc: string | null = null;
  circumference = 2 * Math.PI * 28;
  dashOffset = this.circumference;

  private stream: MediaStream | null = null;
  private gif: any;
  private gifFile: File | null = null;
  private recordingStartTime = 0;
  private captureInterval: any;
  private readonly tapThreshold = 500;
  public currentCameraFacingMode: 'user' | 'environment' = 'environment';
  private gifWidth: number = 0;
  private gifHeight: number = 0;
  private frames: { timestamp: number; image: HTMLImageElement }[] = [];
  private _recordingDuration = 5000;

  supportedLenses: string[] = [];
  currentLens: string = 'Wide';

  constructor() {}

  ngOnInit() {
    this.initializeCamera();
  }

  ngOnDestroy() {
    this.stopCamera();
  }

  private async initializeCamera() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );

      await this.refreshSupportedLenses(videoDevices);

      let selectedDevice: MediaDeviceInfo | undefined;

      if (this.currentCameraFacingMode === 'environment') {
        selectedDevice = this.findDeviceForLens(videoDevices, this.currentLens);
      } else {
        selectedDevice = videoDevices.find((device) =>
          device.label.toLowerCase().includes('front')
        );
      }

      const constraints: MediaTrackConstraints = {
        facingMode: this.currentCameraFacingMode,
        ...(selectedDevice && { deviceId: { exact: selectedDevice.deviceId } }),
      };

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: constraints,
      });

      this.videoPreview.nativeElement.srcObject = this.stream;
      this.videoPreview.nativeElement.play();
      this.videoPreview.nativeElement.onloadedmetadata = () => {
        this.isLoading = false;
        this.updateGifDimensions();
      };

      // Refresh supported lenses after getting the stream
      await this.refreshSupportedLenses(videoDevices);
    } catch (error) {
      console.error('Error initializing camera:', error);
      this.isLoading = false;
    }
  }

  private async refreshSupportedLenses(videoDevices: MediaDeviceInfo[]) {
    this.supportedLenses = ['Wide']; // Always include 'Wide' as a default option

    if (this.currentCameraFacingMode === 'environment') {
      const track = this.stream?.getVideoTracks()[0];
      if (track) {
        const capabilities = track.getCapabilities() as any;
        if (
          capabilities &&
          typeof capabilities.zoom === 'object' &&
          capabilities.zoom !== null
        ) {
          const ultraWideDevice = videoDevices.find((device) =>
            device.label.toLowerCase().includes('ultra wide')
          );
          const telephotoDevice = videoDevices.find((device) =>
            device.label.toLowerCase().includes('telephoto')
          );

          if (ultraWideDevice) this.supportedLenses.push('Ultra Wide');
          if (telephotoDevice) this.supportedLenses.push('Telephoto');

          // If no specific lenses were found, but zoom is supported, add a generic "Zoom" option
          if (
            this.supportedLenses.length === 1 &&
            typeof capabilities.zoom.max === 'number' &&
            typeof capabilities.zoom.min === 'number' &&
            capabilities.zoom.max > capabilities.zoom.min
          ) {
            this.supportedLenses.push('Zoom');
          }
        }
      }
    }
  }

  private findDeviceForLens(
    videoDevices: MediaDeviceInfo[],
    lens: string
  ): MediaDeviceInfo | undefined {
    switch (lens) {
      case 'Ultra Wide':
        return videoDevices.find((device) =>
          device.label.toLowerCase().includes('ultra wide')
        );
      case 'Telephoto':
        return videoDevices.find((device) =>
          device.label.toLowerCase().includes('telephoto')
        );
      case 'Zoom':
      case 'Wide':
      default:
        return (
          videoDevices.find(
            (device) =>
              device.label.toLowerCase().includes('wide') &&
              !device.label.toLowerCase().includes('ultra')
          ) || videoDevices[0]
        ); // Fallback to the first device if no wide camera is found
    }
  }

  private updateGifDimensions() {
    const video = this.videoPreview.nativeElement;
    const aspectRatio = video.videoWidth / video.videoHeight;

    // For picture (Full HD)
    if (aspectRatio > this.maxWidth / this.maxHeight) {
      this.gifWidth = this.maxWidth;
      this.gifHeight = Math.round(this.maxWidth / aspectRatio);
    } else {
      this.gifHeight = this.maxHeight;
      this.gifWidth = Math.round(this.maxHeight * aspectRatio);
    }

    // For GIF (480p)
    let gifWidth, gifHeight;
    if (aspectRatio > this.gifMaxWidth / this.gifMaxHeight) {
      gifWidth = this.gifMaxWidth;
      gifHeight = Math.round(this.gifMaxWidth / aspectRatio);
    } else {
      gifHeight = this.gifMaxHeight;
      gifWidth = Math.round(this.gifMaxHeight * aspectRatio);
    }

    // Apply resolution scale only to GIF dimensions
    this.gifWidth = Math.round(gifWidth * this.resolutionScale);
    this.gifHeight = Math.round(gifHeight * this.resolutionScale);

    this.initGif();
  }

  private initGif() {
    this.gif = new GIF({
      workers: 2,
      quality: Math.round(20 - this.initialGifFrameQuality * 19),
      width: this.gifWidth,
      height: this.gifHeight,
      workerScript: 'assets/gif.worker.js',
    });
  }

  private stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  startRecording() {
    if (this.isRecording) return;
    this.isRecording = true;
    this.recordingStartTime = Date.now();
    this.frames = []; // Clear previous frames
    this.initGif(); // Reinitialize GIF with current settings
    this.startCapturingFrames();
    this.animateProgressRing();
  }

  private async captureFrame() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const video = this.videoPreview.nativeElement;

    canvas.width = this.gifWidth;
    canvas.height = this.gifHeight;

    // Calculate the scaling and positioning to maintain aspect ratio
    const videoAspect = video.videoWidth / video.videoHeight;
    const canvasAspect = canvas.width / canvas.height;
    let sx, sy, sWidth, sHeight;

    if (videoAspect > canvasAspect) {
      sHeight = video.videoHeight;
      sWidth = sHeight * canvasAspect;
      sx = (video.videoWidth - sWidth) / 2;
      sy = 0;
    } else {
      sWidth = video.videoWidth;
      sHeight = sWidth / canvasAspect;
      sx = 0;
      sy = (video.videoHeight - sHeight) / 2;
    }

    ctx?.drawImage(
      video,
      sx,
      sy,
      sWidth,
      sHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Create frame without compression
    const frame = await this.createFrame(canvas);

    // Store the frame with its timestamp
    const timestamp = Date.now();
    this.frames.push({ timestamp, image: frame });

    if (timestamp - this.recordingStartTime >= this.recordingDuration) {
      this.stopRecording();
    }
  }

  private createFrame(canvas: HTMLCanvasElement): Promise<HTMLImageElement> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = canvas.toDataURL('image/jpeg', 1.0); // Use maximum quality
    });
  }

  private animateProgressRing() {
    const animationStartTime = Date.now();
    const animate = () => {
      const progress = Math.min(
        (Date.now() - animationStartTime) / this.recordingDuration,
        1
      );
      this.dashOffset = this.circumference * (1 - progress);
      if (this.isRecording && progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  async stopRecording() {
    if (!this.isRecording) return;
    this.isRecording = false;
    this.isProcessing = true;
    clearInterval(this.captureInterval);

    const recordingDuration = Date.now() - this.recordingStartTime;
    recordingDuration < this.tapThreshold
      ? await this.captureSingleImage()
      : this.finalizeGif();
  }

  private finalizeGif() {
    // Sort frames by timestamp before adding to GIF
    this.frames.sort((a, b) => a.timestamp - b.timestamp);

    const actualDuration =
      this.frames[this.frames.length - 1].timestamp - this.frames[0].timestamp;

    // Calculate the actual time between frames
    for (let i = 1; i < this.frames.length; i++) {
      const delay = this.frames[i].timestamp - this.frames[i - 1].timestamp;
      this.gif.addFrame(this.frames[i - 1].image, { delay: delay });
    }

    // Add the last frame
    if (this.frames.length > 0) {
      const lastFrame = this.frames[this.frames.length - 1];
      this.gif.addFrame(lastFrame.image, { delay: 100 }); // Add a small delay for the last frame
    }

    this.gif.on('finished', (blob: Blob) => {
      this.gifFile = new File(
        [blob],
        `animation-${this.recordingStartTime}.gif`,
        {
          type: 'image/gif',
        }
      );
      console.log(`GIF size: ${this.gifFile.size / 1024} kb`);
      console.log(`GIF duration: ${actualDuration} ms`);

      this.previewImageSrc = URL.createObjectURL(this.gifFile);
      this.isProcessing = false;
      this.isPlayingBack = true;
      this.recordingComplete.emit(this.gifFile);
    });
    this.gif.render();
  }

  private startCapturingFrames() {
    this.captureInterval = setInterval(() => {
      if (this.isRecording) this.captureFrame();
    }, 1000 / this.targetFPS);
  }

  private async captureSingleImage() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const video = this.videoPreview.nativeElement;

    // Calculate the aspect ratio
    const videoAspect = video.videoWidth / video.videoHeight;
    const targetAspect = this.maxWidth / this.maxHeight;

    let canvasWidth, canvasHeight;

    if (videoAspect > targetAspect) {
      // Video is wider than target, use full width
      canvasWidth = this.maxWidth;
      canvasHeight = Math.round(this.maxWidth / videoAspect);
    } else {
      // Video is taller than target, use full height
      canvasHeight = this.maxHeight;
      canvasWidth = Math.round(this.maxHeight * videoAspect);
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Draw the full video frame onto the canvas
    ctx?.drawImage(video, 0, 0, canvasWidth, canvasHeight);

    const timestamp = Date.now();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', this.imageQuality)
    );
    if (blob) {
      const file = new File([blob], `image-${timestamp}.jpg`, {
        type: 'image/jpeg',
      });
      console.log(`Original image size: ${file.size / 1024} kb`);

      const compressedFile = await imageCompression(file, {
        maxSizeMB: this.targetPictureSize,
        maxWidthOrHeight: Math.max(this.maxWidth, this.maxHeight),
        useWebWorker: true,
        initialQuality: this.compressionQuality,
      });
      console.log(`Compressed image size: ${compressedFile.size / 1024} kb`);

      this.isProcessing = false;
      this.isPlayingBack = true;
      this.previewImageSrc = URL.createObjectURL(compressedFile);
      this.recordingComplete.emit(compressedFile);
    }
  }

  removeRecording() {
    this.isPlayingBack = false;
    this.gifFile = null;
    this.previewImageSrc = null;
    this.frames = [];
    this.initGif();
    this.dashOffset = this.circumference;
    this.initializeCamera();
  }

  async switchCamera() {
    this.currentCameraFacingMode =
      this.currentCameraFacingMode === 'user' ? 'environment' : 'user';
    this.currentLens = 'Wide'; // Reset to default lens when switching camera
    this.stopCamera();
    await this.initializeCamera();
  }

  async switchLens(lens: string) {
    if (this.currentLens !== lens && this.supportedLenses.includes(lens)) {
      this.currentLens = lens;
      this.stopCamera();
      await this.initializeCamera();
    }
  }

  setImageQuality(quality: number) {
    if (quality >= 0 && quality <= 1) {
      this.imageQuality = quality;
    } else {
      console.warn('Image quality should be between 0 and 1');
    }
  }

  setResolutionScale(scale: number) {
    if (scale > 0) {
      this.resolutionScale = scale;
      this.updateGifDimensions();
    } else {
      console.warn('Resolution scale should be greater than 0');
    }
  }

  setCompressionQuality(quality: number) {
    if (quality >= 0 && quality <= 1) {
      this.compressionQuality = quality;
    } else {
      console.warn('Compression quality should be between 0 and 1');
    }
  }

  setInitialGifFrameQuality(quality: number) {
    if (quality >= 0 && quality <= 1) {
      this.initialGifFrameQuality = quality;
      this.initGif(); // Reinitialize GIF with new quality setting
    } else {
      console.warn('Initial GIF frame quality should be between 0 and 1');
    }
  }
}
